import type { ReactNode } from 'react';
import { Num } from '../primitives/Num.tsx';
import { Icon } from '../primitives/Icon.tsx';
import { passthrough } from '../primitives/passthrough.js';

/**
 * PricePreview - what a sample customer would pay, and which rule decided it.
 *
 * The rule name is the component's reason to exist. Somebody editing pricing
 * rules is not asking "what is the price" - they can work that out - they are
 * asking "which of my rules is going to decide it", and a number alone cannot
 * answer that. When several rules match, the one that won is named and the
 * others are listed as also-matched, because "why did it not use the other
 * one?" is the next question and the priority order is the answer.
 */

export interface PricePreviewLabels {
  /** Heading over the figure. */
  price: string;
  winningRule: string;
  listPrice: string;
  alsoMatched: string;
  /** Shown while inputs are incomplete: not zero, which is a price. */
  notPriced: string;
}

export interface PricePreviewProps {
  /** Already formatted, in the record's currency. Null while unknown. */
  price: string | null;
  /** The sentence the server wrote. Present whenever a price is. */
  explanation: string | null;
  /** False when the list price was used because nothing matched. */
  ruleApplied: boolean;
  /** Rules that also matched but lost on priority, in order. */
  alsoMatched?: readonly string[];
  labels: PricePreviewLabels;
  /** The inputs the price was computed from, rendered by the caller. */
  inputs?: ReactNode;
}

export function PricePreview({
  price,
  explanation,
  ruleApplied,
  alsoMatched,
  labels,
  inputs,
  ...rest
}: PricePreviewProps & Record<string, unknown>): JSX.Element {
  return (
    <div {...passthrough(rest)} data-component="PricePreview" className="flex flex-col gap-2">
      {inputs}

      <span className="flex flex-col gap-0.5">
        <span className="text-micro uppercase tracking-label text-ink-label">{labels.price}</span>
        {price === null ? (
          // Not a zero. A zero is a price, and showing one for "we have not
          // worked it out yet" is the difference between a preview and a
          // wrong answer.
          <span data-not-priced className="font-serif text-h2 text-ink-300">
            {labels.notPriced}
          </span>
        ) : (
          <Num data-preview-price className="font-serif text-h2 text-ink">
            {price}
          </Num>
        )}
      </span>

      {explanation ? (
        <p data-winning-rule className="flex flex-col gap-0.5 text-dense text-ink-meta">
          <span className="text-micro uppercase tracking-label text-ink-label">
            {ruleApplied ? labels.winningRule : labels.listPrice}
          </span>
          {explanation}
        </p>
      ) : null}

      {alsoMatched && alsoMatched.length > 0 ? (
        <div data-also-matched className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1 text-meta text-warning-text">
            <Icon name="Warning" size="inline" decorative />
            {labels.alsoMatched}
          </span>
          <ul className="flex flex-col">
            {alsoMatched.map((name) => (
              <li key={name} className="text-meta text-ink-meta">
                {name}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
